<template>
  <el-dialog :title="$t('message.cashbackCreateLink')" :visible.sync="dialogVisible" width="500px"
    :close-on-click-modal="false" :close-on-press-escape="false" :show-close="!submitting"
    append-to-body custom-class="purchase-cashback-create-dialog">
    <el-form ref="form" :model="form" :rules="rules" label-width="100px"
      @submit.native.prevent>
      <!-- 交易号与金额、原因共用表单标签宽度，保持两列对齐。 -->
      <el-form-item :label="$t('message.transactionNumber')">
        <div class="purchase-info">
          <span>{{ purchaseNo }}</span>
          <el-button type="text" size="mini" @click="copy(purchaseNo)">{{ $t('message.copy') }}</el-button>
        </div>
      </el-form-item>
      <!-- 使用文本输入保留金额字符串，不通过浮点数转换或计算。 -->
      <el-form-item :label="$t('message.cashbackAmountYuan')" prop="amount">
        <el-input :value="form.amount" inputmode="decimal" :disabled="submitting || retryRequired"
          :placeholder="$t('message.cashbackAmountPlaceholder')" @input="handleAmountInput" @blur="handleAmountBlur" />
      </el-form-item>
      <el-form-item :label="$t('message.cashbackCollectionReason')" prop="reason">
        <el-input v-model="form.reason" type="textarea" :rows="5" resize="vertical"
          :disabled="submitting || retryRequired" :placeholder="$t('message.cashbackReasonPlaceholder')" />
      </el-form-item>
    </el-form>
    <!-- 结果不确定时只允许按原参数重试，防止生成重复的收款链接。 -->
    <p v-if="retryRequired" class="retry-message">{{ $t('message.cashbackCreateUnconfirmed') }}</p>
    <div slot="footer" class="dialog-footer">
      <el-button v-if="isAuth(782)" type="success" :loading="submitting" @click="submitForm">
        {{ retryRequired ? $t('message.cashbackListRetry') : $t('message.cashbackGenerateNow') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      retryRequired: false,
      purchaseNo: '',
      form: { amount: '', reason: '' },
      requestData: null,
      // 按交易号保留尚未确认的请求，关闭再打开仍使用原请求键和参数。
      pendingRequests: {},
    }
  },
  computed: {
    rules() {
      return {
        amount: [{ required: true, validator: this.validateAmount, trigger: 'blur' }],
        reason: [{ required: true, validator: this.validateReason, trigger: 'blur' }],
      }
    },
  },
  methods: {
    // 新建意图重置表单；上次请求结果未确认时恢复原参数继续重试。
    open(purchaseNo) {
      if (!this.isAuth(782) || this.submitting) return
      this.purchaseNo = String(purchaseNo)
      this.requestData = this.pendingRequests[this.purchaseNo] || null
      this.retryRequired = this.requestData !== null
      this.form = this.requestData
        ? { amount: this.requestData.amount, reason: this.requestData.reason }
        : { amount: '', reason: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form.clearValidate())
    },
    // 输入时拒绝文字、负号、科学计数法及超限位数；保留 0. 等小数输入中间状态。
    handleAmountInput(value) {
      const amount = value.replace(/^0+(?=\d)/, '').replace(/^\./, '0.')
      if (/^\d{0,8}(\.\d{0,2})?$/.test(amount)) this.form.amount = amount
    },
    // 失焦时清除零金额并去掉末尾小数点，正数规则仍由表单校验兜底。
    handleAmountBlur() {
      const amount = this.form.amount.replace(/\.$/, '')
      this.form.amount = /[1-9]/.test(amount) ? amount : ''
    },
    // 通过字符串校验正金额、两位小数及上限，避免浮点数精度问题。
    validateAmount(rule, value, callback) {
      const integer = value.split('.')[0].replace(/^0+/, '')
      if (!/^\d+(\.\d{1,2})?$/.test(value) || !/[1-9]/.test(value) || integer.length > 8) {
        return callback(new Error(this.$t('message.cashbackAmountInvalid')))
      }
      callback()
    },
    // 原因按 UTF-8 字节数限制，不能用字符数代替；提交时保留原文及换行。
    validateReason(rule, value, callback) {
      if (!value.trim()) return callback(new Error(this.$t('message.cashbackReasonRequired')))
      if (new TextEncoder().encode(value).length > 60000) {
        return callback(new Error(this.$t('message.cashbackReasonTooLong')))
      }
      callback()
    },
    // 生成 32 位随机键，满足接口 16～80 位幂等键要求。
    createRequestKey() {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16))
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    },
    submitForm() {
      // 782：创建权限同时校验入口和提交，避免仅隐藏按钮仍触发请求。
      if (!this.isAuth(782) || this.submitting) return
      this.$refs.form.validate(async valid => {
        if (!valid || !this.isAuth(782) || this.submitting) return
        this.submitting = true
        try {
          // 仅首次提交或明确失败后修改参数才换键；双击和原参数重试复用原键。
          if (!this.requestData || this.requestData.amount !== this.form.amount || this.requestData.reason !== this.form.reason) {
            this.requestData = {
              purchase_no: this.purchaseNo,
              amount: this.form.amount,
              reason: this.form.reason,
              request_key: this.createRequestKey(),
            }
          }
          this.$set(this.pendingRequests, this.purchaseNo, { ...this.requestData })
          const res = await this.$api.problemApi.purchaseCashbackCreate({ ...this.requestData })
          this.$delete(this.pendingRequests, this.purchaseNo)
          this.retryRequired = false
          if (res.code !== 0) return
          this.$message.success(res.msg)
          this.dialogVisible = false
          this.$emit('created', this.purchaseNo)
        } catch (error) {
          // 超时/网络错误无法确定是否已创建，锁定参数并保留原键供重试。
          this.retryRequired = this.pendingRequests[this.purchaseNo] !== undefined
        } finally {
          this.submitting = false
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.purchase-info {
  display: flex;
  align-items: center;
  gap: 12px;
  word-break: break-all;
}

.dialog-footer {
  text-align: center;
}

.retry-message {
  color: #E6A23C;
  line-height: 1.6;
}
</style>

<style>
/* append-to-body 的弹框使用独立类名适配窄屏。 */
.purchase-cashback-create-dialog {
  max-width: calc(100% - 40px);
}
</style>
